import { FieldInputProps } from 'formik';
import { Maybe } from '../../../../../../../types';
import { OutreachTypes } from '../../../../../../../urls';
import {
  ConfigurationEmailResponse,
  OutreachMetadata,
  OutreachTriggerTypes,
  SupportOutreachTriggerFormFields,
  TriggerTypeRendererGenericProps,
} from '../../../../../types';
import {
  FrequencyUnits,
  isTriggerTypeAvailable,
  possibleFrequencyUnits,
} from '../../../helpers';
import SupportGenericTrigger from '../triggers/SupportGenericTrigger';
import SupportTimeTrigger from '../triggers/SupportTimeTrigger';
import {
  DropdownProperties,
  DropdownProperty,
  FormikSupportOutreachTriggerValue,
} from './types';
import { FilterDropdownItem } from 'plume-ui';
import { FunctionComponent } from 'react';

export const mapTriggerTypeToRenderer: Record<
  OutreachTriggerTypes,
  FunctionComponent<TriggerTypeRendererGenericProps>
> = {
  [OutreachTriggerTypes.Time]: SupportTimeTrigger,
  [OutreachTriggerTypes.MaxValue]: SupportGenericTrigger,
  [OutreachTriggerTypes.MaxPercentage]: SupportGenericTrigger,
  [OutreachTriggerTypes.MaxIncreaseRate]: SupportGenericTrigger,
  [OutreachTriggerTypes.CSPOutageDetected]: SupportGenericTrigger,
};

export const triggerDropdownsPossibleValues: Record<
  DropdownProperties,
  DropdownProperty[]
> = {
  [SupportOutreachTriggerFormFields.TriggerType]: [
    {
      id: OutreachTriggerTypes.Time,
      labelId: 'outreach.form.common.triggerTypes.time',
    },
    {
      id: OutreachTriggerTypes.MaxValue,
      labelId: 'outreach.form.common.triggerTypes.maxValue',
    },
    {
      id: OutreachTriggerTypes.MaxPercentage,
      labelId: 'outreach.form.common.triggerTypes.maxPercentage',
    },
    {
      id: OutreachTriggerTypes.MaxIncreaseRate,
      labelId: 'outreach.form.common.triggerTypes.maxIncreaseRate',
    },
    {
      id: OutreachTriggerTypes.CSPOutageDetected,
      labelId: 'outreach.form.common.triggerTypes.cspOutageDetected',
    },
  ],
  [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit]: possibleFrequencyUnits,
  [SupportOutreachTriggerFormFields.TimeRepeatEveryUnit]: possibleFrequencyUnits,
};

export const retrieveAllowedDropdownOptionsFromMetadata = (
  property: DropdownProperties,
  field: FieldInputProps<FormikSupportOutreachTriggerValue>,
  outreachMetadata: Maybe<OutreachMetadata>,
  outreachMethod: OutreachTypes,
) => {
  let allowedValuesFromMetadata;
  let values = triggerDropdownsPossibleValues[property];
  const triggerType = field.value[SupportOutreachTriggerFormFields.TriggerType];
  const outreachSupportMetadata = outreachMetadata![OutreachTypes.Email];
  if (
    triggerType === OutreachTriggerTypes.Time &&
    [
      SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit,
      SupportOutreachTriggerFormFields.TimeRepeatEveryUnit,
    ].includes(property)
  ) {
    allowedValuesFromMetadata =
      property === SupportOutreachTriggerFormFields.TimeRepeatEveryUnit
        ? (outreachSupportMetadata.time.allowedInputs.schedule as any)
            .allowedInputs.recurrenceUnit.allowedValues
        : outreachSupportMetadata.maxFrequency.allowedInputs.recurrenceUnit
            .allowedValues;
  } else if (
    triggerType !== OutreachTriggerTypes.Time &&
    property ===
      SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit
  ) {
    allowedValuesFromMetadata =
      outreachSupportMetadata.maxFrequency.allowedInputs.recurrenceUnit
        .allowedValues;
  }

  if (
    allowedValuesFromMetadata &&
    [
      SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit,
      SupportOutreachTriggerFormFields.TimeRepeatEveryUnit,
    ].includes(property)
  ) {
    const allowedValuesKeys = allowedValuesFromMetadata.map((v: any) => v.key);
    values = values.filter((v) => allowedValuesKeys.includes(v.id));
  }

  if (property === SupportOutreachTriggerFormFields.TriggerType) {
    values = values.filter(
      (v: DropdownProperty) =>
        outreachMetadata &&
        isTriggerTypeAvailable(
          v.id as OutreachTriggerTypes,
          outreachMetadata,
          outreachMethod,
        ),
    );
  }

  if (
    [
      SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit,
      SupportOutreachTriggerFormFields.TimeRepeatEveryUnit,
    ].includes(property)
  ) {
    // removing Once
    values = values.filter(
      (v: DropdownProperty) => v.id !== FrequencyUnits.Once,
    );
  }

  return values;
};

export const parseEmailsToDropdownOptions = (
  emails: Maybe<ConfigurationEmailResponse[]>,
): FilterDropdownItem[] =>
  emails?.map((email) => ({
    value: email.id,
    label: `${email.name}<${email.email}>`,
  })) || [];
